import {
  Table,
  TableBody,
  TableCell,
  TableHead,
} from '@src/common/components/Table';
import { TableRow } from '@src/common/components/TableRow';
import { css } from '@src/common/styles/Styles';
import { theme } from '@src/common/styles/Theme';
import type { ReactNode } from 'react';

export type ColumnContent = {
  content: ReactNode;
  showRightBorder?: boolean;
};

export type RowDefinition = {
  id: string;
  columnsContent: ColumnContent[];
};

export type RowWithSubRows = {
  subRows?: RowDefinition[];
} & RowDefinition;

export type ColumnHeader = {
  header: string | ReactNode;
  showRightBorder?: boolean;
  minWidth?: string;
};

interface Props {
  columnsName: ColumnHeader[];
  rows: RowWithSubRows[];
  ariaLabelledBy?: string;
}

export const TableWithSubRows = ({
  columnsName,
  rows,
  ariaLabelledBy,
}: Props) => {
  return (
    <Table aria-labelledby={ariaLabelledBy} className={table}>
      <TableHead>
        <TableRow key={`${ariaLabelledBy}-header-row`}>
          {columnsName.map(({ showRightBorder, minWidth, header }, i) => (
            <TableCell
              key={`${ariaLabelledBy}-column-${i}`}
              className={showRightBorder ? headerWithBorder : undefined}
              sx={{
                borderBottom: theme.table.border.secondary,
                borderTop: theme.table.border.primary,
                minWidth,
              }}
            >
              {header}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>

      <TableBody>
        {rows.map((row, i) => (
          <Row row={row} key={`body-row-${i}`} />
        ))}
      </TableBody>
    </Table>
  );
};

const Row = ({ row }: { row: RowWithSubRows }) => {
  const id = row.id;

  return (
    <>
      <TableRow key={`row-${id}`} className={rowStyle}>
        {row.columnsContent.map(({ content, showRightBorder }, i) => (
          <TableCell
            key={`row-${id}-column-${i}`}
            className={showRightBorder ? tableCellWithBorder : tableCell}
          >
            {content}
          </TableCell>
        ))}
      </TableRow>

      {row.subRows?.map((subRow) => (
        <TableRow key={`sub-row-${subRow.id}`}>
          {subRow.columnsContent.map(
            ({ content, showRightBorder }, srColIndex) => (
              <TableCell
                key={`sub-row-${subRow.id}-column-${srColIndex}`}
                className={showRightBorder ? tableCellWithBorder : tableCell}
              >
                {content}
              </TableCell>
            ),
          )}
        </TableRow>
      ))}
    </>
  );
};

const table = css({
  border: 0,
});

const tableCell = css({
  padding: `${theme.spacing['3']}`,
  border: 0,
});

const tableCellWithBorder = css({
  padding: `${theme.spacing['3']}`,
  border: 0,
  borderRight: `1px solid ${theme.palette.surface.outlineVariant}`,
});

const rowStyle = css({
  borderTop: `1px solid ${theme.palette.surface.outlineVariant}`,
});

const headerWithBorder = css({
  borderRight: `1px solid ${theme.palette.surface.outlineVariant}`,
});
